import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";
import { MOBILE_QUERY } from "../layout";

import {
  HostQaRequestError,
  type HostQuestion,
  type OverlayState,
  audienceUrlFor,
  getQaRoom,
  moderateQuestion,
  openQa,
  setOverlay,
  closeQa,
  setQaOpen,
  streamQuestions,
} from "../qa/host-client";

// Audience Q&A, in the host controller's rail.
//
// Built as its own component rather than inline in controller.tsx, which is
// already 6,000 lines: the panel has its own connection lifecycle, its own error
// states and its own moderation actions, none of which the controller needs to
// know about. It is handed the two credentials and the plan, and reports back
// only the room code — which the overlay card will need.
//
// The host reads this mid-sentence, so every state has to survive a two-second
// glance: what the code is, how many questions are waiting, and which one to
// take next.

/** Reconnect backoff after a stream ends. Cloud Run cuts every stream eventually. */
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15000;

/**
 * Auto-hide presets, not a time picker.
 *
 * The host is mid-sentence with one hand on a phone. A duration input is
 * unoperable in that moment; four taps that each mean something are not.
 */
const PRESETS: { label: string; seconds: number | null }[] = [
  { label: "30s", seconds: 30 },
  { label: "1 min", seconds: 60 },
  { label: "2 min", seconds: 120 },
  { label: "Until I hide it", seconds: null },
];

// No divider and no top margin: this is its own section of the rail now, not a
// block appended below the copresenter invite. The switcher above it is what
// separates it from the rest.
const Section = styled.div`
  display: flex;
  flex-direction: column;
  /* Fills the rail's panel, which is what gives Empty a height to centre in.
     Harmless for the running panel: its children stack from the top as before. */
  flex: 1 1 auto;
  min-height: 0;
`;

const Label = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--text-fade);
  margin-bottom: 6px;
`;

/**
 * The empty state, before there is a room.
 *
 * Centred, unlike everything else in this panel, and deliberately: the running
 * panel is a work surface — a queue to scan down the left edge of — while this
 * is a single proposition with one thing to do about it. Centring is what says
 * "read this, then press that" rather than "start scanning".
 */
const Empty = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2px 0 24px;
`;

/**
 * Everything below the title, centred in whatever height is left.
 *
 * The title stays at the top because it labels the SECTION — it is the same
 * anchor the running panel's first heading is, and letting it float with the
 * artwork would make the panel's top edge move depending on how tall the
 * content happened to be. The pitch below it is one proposition with one thing
 * to do about it, so that centres.
 */
const EmptyBody = styled.div`
  flex: 1 1 auto;
  /* Stretch, not the parent's centring: the CTA is full width and would
     otherwise measure itself against the widest line of prose. */
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* One gap for all three, rather than a margin on each. Picture, sentence and
     button are three separate beats of the same pitch, and spacing them evenly
     is what lets the eye move down them in order — uneven gaps group two of the
     three together and make the odd one out look like an afterthought. */
  gap: 22px;
`;

const EmptyTitle = styled.h3`
  margin: 0;
  /* The same 16px as every other panel header in the rail. Bigger made this one
     panel announce itself louder than the section the host clicked to get here. */
  font-size: 16px;
  font-weight: 700;
  /* Upper case, and tracked out to match: at this weight the caps close up and
     read as a block rather than as a word. */
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-headline);

  /* Gone on a phone. The sheet is opened from a tab labelled "Q&A" and arrives
     with that tab still on screen, so the title repeats a word the host pressed
     a second ago — and spends the top of a short sheet doing it. The desktop
     rail keeps it: there the panel is a column with no heading above it. */
  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

const EmptyText = styled.p`
  /* A measure, not a width: centred prose wider than about 30 characters makes
     the eye hunt for the start of each line. */
  max-width: 28ch;
  /* No margin — EmptyBody's gap owns the rhythm here. */
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-muted);
`;

/**
 * What Q&A actually is, in one picture: the deck on a screen with the join code
 * on it, and questions coming back from the room.
 *
 * Drawn from theme tokens rather than a flat image so it holds up in both
 * themes, and stroked in the same weight as the app's icons so it reads as part
 * of the product rather than as marketing dropped into it.
 */
const QaIllustration = () => (
  <svg
    width="132"
    height="104"
    viewBox="0 0 132 104"
    fill="none"
    aria-hidden="true"
  >
    {/* The presented screen */}
    <rect
      x="15"
      y="3"
      width="102"
      height="60"
      rx="8"
      stroke="var(--border-chip)"
      strokeWidth="2"
    />
    {/* Two lines of slide, kept vague — this is any deck, not a specific one */}
    <path
      d="M29 21h40M29 32h26"
      stroke="var(--border-chip)"
      strokeWidth="3.5"
      strokeLinecap="round"
    />
    {/* The join code, on the screen where the room reads it */}
    <rect x="79" y="38" width="26" height="15" rx="4" fill="var(--link)" />

    {/* Questions coming back */}
    <rect
      x="16"
      y="72"
      width="42"
      height="23"
      rx="7"
      fill="var(--bg-card)"
      stroke="var(--border-chip)"
      strokeWidth="2"
    />
    <path d="M27 95v7l9-7z" fill="var(--bg-card)" stroke="var(--border-chip)" strokeWidth="2" />
    <rect x="68" y="66" width="48" height="26" rx="8" fill="var(--link)" />
    <path d="M104 92v8l-10-8z" fill="var(--link)" />
  </svg>
);

const Action = styled.button`
  width: 100%;
  border: none;
  border-radius: 9px;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  background: var(--link);
  color: #fff;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;


/**
 * The room, as one card: the code, and the one thing you do with it.
 *
 * These were two blocks — a white join card, then a separate "Show QR code on deck"
 * section under it — and the split was in the wrong place. Putting the code up
 * on the projector is not a neighbouring feature, it is an ACTION ON THE CODE,
 * and separating them spaced it exactly as far from the code as the panel's
 * destructive controls were. One card says what belongs to what without needing
 * a heading to explain it.
 *
 * Dark, unlike the white slab it replaces. Only the QR genuinely needs a light
 * plate to scan; making the whole card white to satisfy it put the loudest
 * surface in the rail around the panel's most static content, so the eye landed
 * there first every time it entered the section, long after the host had
 * finished caring about the code.
 */
const RoomCard = styled.div`
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  background: var(--bg-card);
  overflow: hidden;
`;

// Three bands, each with its own job: what the code IS, how to send it, how to
// put it on the screen. The rules are full-bleed rather than inset so the card
// reads as stacked rows rather than as one box with lines drawn inside it.
const JoinRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
`;

const ShareSection = styled.div`
  padding: 11px 12px;
`;

/**
 * The one bright surface, and no bigger than it has to be: a QR on a dark plate
 * does not scan, so this stays white in both themes.
 *
 * GONE on a phone. Nobody scans a QR off the presenter's own handset — the one
 * the room scans is on the deck, and this panel can put it there. All it did on
 * a phone was take a third of the card's width, and the brightest thing on the
 * screen, to show the host something they cannot use.
 */
const QrTile = styled.div`
  flex: 0 0 auto;
  display: flex;
  padding: 6px;
  border-radius: 9px;
  background: #fff;

  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

const JoinText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

// The hero: the largest, brightest text in the panel, because it is the one
// thing the host reads out loud. Sized to FIT — a code clipped mid-character is
// worse than a smaller one — which is why it moved with the rail: at 304px it
// had about 100px beside the QR, and at 352 it has room to be read.
const Code = styled.span`
  font-size: 23px;
  font-weight: 700;
  letter-spacing: 0.07em;
  line-height: 1.1;
  color: var(--text-headline);
  font-variant-numeric: tabular-nums;

  /* The QR is gone here, so the whole card's width is the code's — and this is
     the number a host reads out to a room while holding the phone. */
  @media ${MOBILE_QUERY} {
    font-size: 26px;
  }
`;

/**
 * The link, as a field you can copy — not a line of grey text under the code.
 *
 * It was the latter, and that made it useless twice over: too small to read
 * aloud, too long to type, and impossible to get into the chat window where a
 * remote audience actually is. Wrapping broke it mid-code as well ("…/q/T" /
 * "NZRA1"), which reads as two fragments and invites someone to type the first
 * half.
 *
 * So it becomes what a shareable link is everywhere else in this app: a
 * selectable field with a copy button beside it, matching the invite row in the
 * roster rail so the two behave the same way.
 */
const ShareRow = styled.div`
  display: flex;
  gap: 6px;

  @media ${MOBILE_QUERY} {
    gap: 8px;
  }
`;

const ShareInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-page);
  color: var(--text-muted);
  padding: 7px 9px;
  font-size: 11.5px;
  font-family: inherit;
  text-overflow: ellipsis;

  /* Gone on a phone, exactly as the roster's invite field is: nobody reads a
     URL out of a truncated input or types it, they tap Copy and paste it into
     whatever chat is open. Copy becomes the whole control instead. */
  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

const CopyButton = styled.button<{ $done: boolean }>`
  appearance: none;
  cursor: pointer;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 34px;
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: transparent;
  color: ${(p) => (p.$done ? "var(--link)" : "var(--text-muted)")};
  padding: 0;
  font-family: inherit;

  &:hover {
    color: var(--link);
    border-color: var(--link);
  }

  @media ${MOBILE_QUERY} {
    flex: 1 1 auto;
    width: auto;
    min-height: 44px;
    font-size: 13px;
    font-weight: 600;
  }
`;

// Only where the field is hidden — beside the field it would say what the field
// already shows.
const CopyLabel = styled.span`
  display: none;

  @media ${MOBILE_QUERY} {
    display: inline;
  }
`;

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

// The platform share glyph — the box-with-an-arrow both phone OSes use for this
// exact gesture — at the stroke weight of the icons around it.
const ShareIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" />
    <path d="M8 7l4-4 4 4" />
    <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
  </svg>
);

const CopiedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
    stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const CardRule = styled.div`
  height: 1px;
  background: var(--border-soft);
`;

const DeckSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 11px 12px 12px;
`;

// Quieter than the panel's own group headings — this labels a row inside a card,
// not a section of the rail, and matching the outer headings would flatten the
// two levels into one.
const CardLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-fade);
`;

/**
 * One step down the hierarchy from the join card, and the unit of rhythm for
 * the whole panel: every group below is separated by the same gap, so the eye
 * can find the boundaries without a rule under each heading.
 */
const Group = styled.section<{ $stageFirst?: boolean }>`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 9px;

  ${(p) =>
    p.$stageFirst &&
    `@media ${MOBILE_QUERY} {
       order: 0;
       margin-top: 4px;
     }`}
`;

/**
 * The join card and the auto-hide presets, as one block — so a phone can move
 * both below the queue in a single step.
 *
 * On a laptop, before the talk, showing the code first is right: it is the
 * thing being set up. On a phone during the talk it is exactly wrong. Getting
 * the room in is done once, in the first minute; reading and clearing questions
 * happens for the next forty, one-handed, from the back of a stage. Whatever
 * that person needs most has to be under their thumb when the sheet opens, and
 * it is never the QR they already showed.
 */
const SetupBlock = styled.div`
  display: contents;

  @media ${MOBILE_QUERY} {
    display: block;
    order: 1;
    margin-top: 22px;
  }
`;

const GroupHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const Waiting = styled.span`
  font-size: 11.5px;
  color: var(--text-fade);
  font-variant-numeric: tabular-nums;
`;

const Questions = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 340px;
  overflow-y: auto;

  /* No inner scroller on a phone: the sheet already scrolls, and a scroll
     region inside a scroll region is how a thumb-flick ends up moving neither. */
  @media ${MOBILE_QUERY} {
    max-height: none;
    overflow: visible;
    gap: 10px;
  }
`;

const Item = styled.li<{ $dim: boolean }>`
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  padding: 10px 11px;
  background: var(--bg-card);
  opacity: ${(p) => (p.$dim ? 0.45 : 1)};

  @media ${MOBILE_QUERY} {
    border-radius: 12px;
    padding: 13px 14px;
  }
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  color: var(--text-fade);

  @media ${MOBILE_QUERY} {
    font-size: 12.5px;
  }
`;

const Votes = styled.span`
  font-weight: 700;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
`;

const QuestionText = styled.p`
  margin: 5px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-primary);
  overflow-wrap: anywhere;

  /* The one thing on this panel that gets READ rather than glanced at, by
     someone holding the phone at arm's length under stage lighting. */
  @media ${MOBILE_QUERY} {
    margin-top: 7px;
    font-size: 16px;
    line-height: 1.45;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 9px;

  @media ${MOBILE_QUERY} {
    gap: 8px;
    margin-top: 12px;
  }
`;

const MiniButton = styled.button<{ $active?: boolean }>`
  border: 1px solid ${(p) => (p.$active ? "var(--link)" : "var(--border-chip)")};
  background: ${(p) => (p.$active ? "var(--link)" : "transparent")};
  color: ${(p) => (p.$active ? "#fff" : "var(--text-muted)")};
  border-radius: 7px;
  padding: 4px 9px;
  font-size: 11px;
  font-family: inherit;
  cursor: pointer;

  /* Marking a question answered is the action a speaker takes MID-SENTENCE,
     one-handed, without looking down for long. At 22px tall it was a target
     you had to aim at; these three now split the row at 44px.

     Deliberately equal weight rather than one primary: hiding a hostile
     question is every bit as urgent as ticking one off, and a speaker reaching
     for it should not have to find the smaller button. */
  @media ${MOBILE_QUERY} {
    flex: 1 1 0;
    min-height: 44px;
    border-radius: 10px;
    padding: 4px 6px;
    font-size: 13px;
    font-weight: 600;
  }
`;

const Problem = styled.p`
  /* Ahead of everything, on both layouts: an error the host cannot see is an
     error they will act on the wrong way. */
  order: -1;
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #ef4444;
`;

// Three timed presets as equal peers, then the open-ended one across the full
// width. It is a different KIND of choice, and laying it out as a fourth
// equal-width chip both cramped the label and implied it was another duration.
const Presets = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
`;

// Chips, not buttons. Inside the card these are options attached to the code
// above them; at the panel's full button weight, four outlined boxes competed
// with the two real decisions at the foot and made the section look like six
// equally important things to press.
const Preset = styled.button<{ $wide?: boolean }>`
  ${(p) => p.$wide && "grid-column: 1 / -1;"}

  @media ${MOBILE_QUERY} {
    min-height: 44px;
    font-size: 13px;
  }
  border: 1px solid var(--border-soft);
  background: transparent;
  color: var(--text-muted);
  border-radius: 8px;
  padding: 7px 6px;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;

  &:hover:enabled {
    border-color: var(--link);
    color: var(--link);
  }
`;

/**
 * The code is UP — the one state in this panel that describes something the
 * room can see right now.
 *
 * It used to be a grey sentence and a button: "Hiding in 18s" carried the whole
 * message, in the same weight as a form label, and the panel gave no sign that
 * anything was live. The host glances at this while talking, and the question
 * they are answering is "is it still up, and for how long" — which is a state
 * to recognise, not a sentence to read.
 *
 * So: a live dot (the signal every camera and stream already uses), the state
 * in words, and the remaining time as a BAR as well as a number. The bar is
 * what makes it readable at a glance — you can see "nearly gone" from across a
 * lectern without focusing on two digits.
 *
 * Inset rather than raised: it sits inside the card, so it borrows the page's
 * ground to read as a well. A card-coloured fill on a card is invisible.
 */
const LiveTile = styled.div`
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-page);
  overflow: hidden;
`;

const LiveRow = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 7px 7px 11px;
`;

/**
 * On-air. A ring that pulses outward from a solid centre — the same idiom as a
 * recording light, which is exactly what this is.
 *
 * Motion is the point (a static dot is just a bullet), so it is also the first
 * thing to go for anyone who has asked the OS for less of it.
 */
const LiveDot = styled.span`
  position: relative;
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--link);

  &::after {
    content: "";
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 1.5px solid var(--link);
    animation: qaLivePulse 1.8s ease-out infinite;
  }

  @keyframes qaLivePulse {
    0% { transform: scale(0.6); opacity: 0.9; }
    100% { transform: scale(1.4); opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
      opacity: 0.35;
    }
  }
`;

/**
 * Label and countdown as ONE shrinkable group, wrapping between themselves.
 *
 * They used to be two independent flex children next to a button, which made
 * the label the only thing in the row that could give — so in a narrow rail it
 * gave, and "On your deck" came apart into three stacked words beside a countdown
 * that had lost nothing. Wrapping here means the countdown drops to its own
 * line first and the words stay whole, which is the order that keeps the row
 * readable at any width.
 */
const LiveText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 8px;
`;

// The state, in words. Present tense and about the DECK, not about this panel:
// what the host is checking is the screen behind them.
const LiveLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-headline);
  /* Never broken across lines. It is three short words that mean one thing. */
  white-space: nowrap;
`;

// Tabular so the row does not twitch as the digits change — a number that
// shifts its neighbours every second reads as a glitch in the corner of the eye.
const Countdown = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const HideNow = styled(MiniButton)`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;

  @media ${MOBILE_QUERY} {
    /* Not the flex:1 the moderation buttons take — this shares a row with a
       label and a countdown, and stretching it would push both out. */
    flex: 0 0 auto;
    padding: 4px 12px;
  }
`;

/**
 * The countdown, as a shape.
 *
 * Depletes left to right over the life of the overlay. `transition` rather than
 * a keyframe animation because the width is driven by the same one-second tick
 * the digits are: the transition just glides between the steps, so the bar
 * stays exactly as truthful as the number beside it.
 */
const LiveBar = styled.div<{ $pct: number }>`
  height: 3px;
  background: var(--border-soft);

  &::after {
    content: "";
    display: block;
    height: 100%;
    width: ${(p) => Math.max(0, Math.min(100, p.$pct))}%;
    background: var(--link);
    transition: width 1s linear;
  }
`;

// Screen-reader-only text. The countdown beside it is aria-hidden — read aloud
// every second it would be noise, not information — so the state a listener
// needs rides along with the label instead.
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const EyeOffIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"
    stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3l18 18" />
    <path d="M10.6 6.2A9.6 9.6 0 0 1 12 6c5 0 9 4.5 9 6a11 11 0 0 1-2.4 3.3" />
    <path d="M6.5 8A11.7 11.7 0 0 0 3 12c0 1.5 4 6 9 6a9.4 9.4 0 0 0 3.5-.7" />
    <path d="M9.9 10.1a3 3 0 0 0 4.1 4.2" />
  </svg>
);

// The session-ending action, at the foot and quiet. It used to sit directly
// under the code, where the loudest control on screen was the one that turns
// the feature off.
// Stacked, not side by side. These two read as a pair of equals when they sit
// on one row, and they are not: one pauses the room, the other destroys it.
// Full width also keeps the destructive label intact instead of truncating it
// into something ambiguous on a narrow panel.
const Foot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid var(--border-soft);

  @media ${MOBILE_QUERY} {
    order: 2;
  }
`;

const QuietAction = styled.button<{ $danger?: boolean; $armed?: boolean }>`
  width: 100%;
  min-width: 0;
  border: 1px solid var(--border-chip);
  border-radius: 9px;
  padding: 8px 12px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  background: ${(p) => (p.$armed ? "#ef4444" : "transparent")};
  color: ${(p) => {
    if (p.$armed) return "#fff";
    return p.$danger ? "#ef4444" : "var(--text-muted)";
  }};
  border-color: ${(p) => (p.$danger || p.$armed ? "#ef4444" : "var(--border-chip)")};
  cursor: pointer;

  &:hover:enabled {
    color: ${(p) => {
      if (p.$armed) return "#fff";
      return p.$danger ? "#ef4444" : "var(--text-primary)";
    }};
    background: ${(p) => (p.$armed ? "#dc2626" : p.$danger ? "rgba(239, 68, 68, 0.1)" : "transparent")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media ${MOBILE_QUERY} {
    min-height: 44px;
    font-size: 13px;
  }
`;

const ClosedNote = styled.p`
  margin: 0 0 12px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--text-muted);
`;

export interface QaPanelProps {
  readonly magicId: string | null;
  /** False for a free host: the panel shows the offer rather than the feature. */
  readonly isPro: boolean;
  readonly onUpgrade: (source: string) => void;
  /**
   * Reports the live room, for the invite surfaces and for the badge on the
   * rail's Q&A tab. `waiting` is why this panel stays mounted while its section
   * is hidden: a tab that cannot say how many questions are queued makes hiding
   * Q&A behind it a way to miss the room entirely.
   *
   * `showingCode` is the overlay, for the slide preview — the host presses a
   * preset here and the thing it affects is on another screen entirely.
   */
  readonly onRoomChange?: (
    room: {
      code: string;
      open: boolean;
      waiting: number;
      showingCode: boolean;
    } | null,
  ) => void;
  readonly capture?: (event: string, props?: Record<string, unknown>) => void;
}

export const QaPanel = ({
  magicId,
  isPro,
  onUpgrade,
  onRoomChange,
  capture,
}: QaPanelProps) => {
  const [room, setRoom] = useState<{ code: string; open: boolean } | null>(null);
  const [questions, setQuestions] = useState<HostQuestion[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [overlay, setOverlayState] = useState<OverlayState>({ visible: false, hideAt: null });
  // Ticks once a second only while a countdown is running, to re-render it.
  const [, setTick] = useState(0);

  // Whether the room this session already has is still being fetched. Starts
  // true so the first paint after a reload never offers to turn on something
  // that is already on — see the restore effect.
  const [restoring, setRestoring] = useState(true);

  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<number | undefined>(undefined);
  const restored = useRef(false);

  // "Copied", briefly. Cleared on unmount as well as on the timer: the panel
  // stays mounted while its section is hidden, but the rail does tear it down,
  // and a setState after that is a warning in the console for a tick of
  // feedback nobody is looking at any more.
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | undefined>(undefined);
  useEffect(
    () => () => {
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    },
    [],
  );

  /**
   * Phone + a browser with a share sheet — the same test the invite link makes.
   *
   * Read live rather than once: the panel is the same component on a rotated
   * phone and a dragged-narrow window, and feature-detecting navigator.share is
   * the only honest test of whether a sheet exists at all.
   */
  const [isPhone, setIsPhone] = useState<boolean>(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const handler = () => setIsPhone(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const canShare = isPhone && typeof navigator.share === "function";

  /**
   * The join link, through the phone's own share sheet.
   *
   * Same reasoning as the invite link in the rail beside this: on a phone the
   * clipboard is a promise the app cannot keep — the host still has to leave,
   * find the thread and paste, and anything copied on the way eats it. Desktop
   * keeps Copy, where the link is on screen in a field next to the button.
   */
  const shareLink = async (url: string) => {
    capture?.("qa_link_shared");
    try {
      await navigator.share({ title: "Ask a question", url });
    } catch (error) {
      // Dismissing the sheet is the ordinary way to close one — not a failure,
      // and not a reason to write to a clipboard the host did not ask for.
      if ((error as DOMException | undefined)?.name === "AbortError") return;
      copyShareLink(url);
    }
  };

  const copyShareLink = (url: string) => {
    const done = () => {
      setCopied(true);
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 1500);
    };
    capture?.("qa_link_copied");
    // Confirms even where the clipboard API is missing or refused. The field is
    // right there and already selectable, so the host's fallback is to select
    // it by hand — a button that silently does nothing tells them the link is
    // broken, which is the one wrong conclusion available here.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(done);
    } else {
      done();
    }
  };

  const pending = questions.filter((q) => !q.hidden && !q.answered).length;

  // Held on a ref so the effect below depends on the DATA, not on the callback's
  // identity. Callers pass an inline arrow more often than not, and depending on
  // that identity would re-report on every render — which, since the caller sets
  // state from it, is a render loop.
  const onRoomChangeRef = useRef(onRoomChange);
  onRoomChangeRef.current = onRoomChange;

  useEffect(() => {
    onRoomChangeRef.current?.(
      room
        ? {
            ...room,
            waiting: pending,
            // Only while questions are open: pausing takes the overlay down at
            // the extension too, so reporting it as still up would put a code on
            // the preview that is not on the deck.
            showingCode: room.open && overlay.visible,
          }
        : null,
    );
  }, [room, pending, overlay.visible]);

  // Come back to the room we left.
  //
  // The controller is a web page, so it gets refreshed — mid-talk, on a phone,
  // by a host whose screen locked. Everything below lives in component state and
  // does not survive that, while the code stays on the projector and the
  // audience keeps typing. Without this the panel returns offering "Turn on
  // Q&A", with no code, no questions and no countdown, for a room that is
  // plainly still running.
  //
  // Once only, tracked on a ref rather than in state: the magic id and the plan
  // both resolve asynchronously after mount, so this effect re-runs a few times
  // during a normal load, and re-fetching after the host has started acting
  // would overwrite the optimistic overlay state with a snapshot taken before
  // their tap. A failure leaves `restored` false so the retry happens when the
  // missing credential arrives.
  useEffect(() => {
    if (restored.current) return undefined;

    if (!magicId || !isPro) {
      // Nothing to restore, and for an unentitled host nothing to restore it
      // into — the panel renders its offer instead.
      setRestoring(false);
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      try {
        const current = await getQaRoom(magicId);
        if (cancelled) return;
        restored.current = true;
        if (current) {
          setRoom({ code: current.code, open: current.open });
          setOverlayState(current.overlay);
        }
      } catch {
        // Couldn't ask. Deliberately silent: the panel falls back to its
        // starting state, and "Start Q&A" recovers the same room and the same
        // code, because opening is idempotent.
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [magicId, isPro]);

  // Stream while the room is open. Reconnects on end, because Cloud Run cuts
  // every request at its timeout — being disconnected mid-presentation is
  // routine, not exceptional, and a panel that stopped updating after an hour
  // would look like the feature quietly died.
  useEffect(() => {
    if (!magicId || !room?.open) return undefined;

    let disposed = false;
    let stop: (() => void) | undefined;

    const connect = () => {
      if (disposed) return;
      stop = streamQuestions(magicId, {
        onQuestions: (next) => {
          reconnectAttempt.current = 0;
          setQuestions(next);
        },
        onError: () => {
          /* Handled by onEnd's backoff; a transient blip needs no banner. */
        },
        onEnd: () => {
          if (disposed) return;
          const delay = Math.min(
            RECONNECT_BASE_MS * 2 ** reconnectAttempt.current,
            RECONNECT_MAX_MS,
          );
          reconnectAttempt.current += 1;
          reconnectTimer.current = window.setTimeout(connect, delay);
        },
      });
    };

    connect();

    return () => {
      disposed = true;
      stop?.();
      window.clearTimeout(reconnectTimer.current);
    };
  }, [magicId, room?.open]);

  const describe = (error: unknown): string => {
    if (error instanceof HostQaRequestError) {
      switch (error.reason) {
        case "sign_in_required":
          return "Sign in to use audience Q&A.";
        case "upgrade_required":
          return "Audience Q&A is part of Copresent Pro.";
        case "needs_host_link":
          return "Open the controller from the extension to manage Q&A.";
        case "no_session":
          return "Start the slideshow first.";
        default:
          return "Couldn't reach the server. Try again.";
      }
    }
    return "Something went wrong. Try again.";
  };

  const turnOn = useCallback(async () => {
    if (!magicId) return;
    setBusy(true);
    setProblem(null);
    try {
      const next = await openQa(magicId);
      setRoom(next);
      capture?.("qa_opened");
    } catch (error) {
      setProblem(describe(error));
    } finally {
      setBusy(false);
    }
  }, [magicId, capture]);

  /**
   * End it. The code is retired, the questions are deleted, and the panel goes
   * back to its empty state — ready for the next audience rather than holding
   * the last one's room.
   *
   * Two taps, and no dialog. What it destroys is irreversible — the questions
   * are deleted, not archived — so a single tap next to "Pause questions" would
   * be one slip away from wiping a room mid-talk. A modal would be worse: this
   * is pressed between talks, one-handed, and a dialog on a phone covers the
   * panel it is asking about. So the button asks for itself, in place, and goes
   * back to normal if the host does anything else.
   */
  const [confirmingClose, setConfirmingClose] = useState(false);

  const endQa = useCallback(async () => {
    if (!magicId) return;
    if (!confirmingClose) {
      setConfirmingClose(true);
      return;
    }

    setConfirmingClose(false);
    setBusy(true);
    setProblem(null);
    try {
      await closeQa(magicId);
      // Everything the panel holds about the room, together: a leftover
      // question list or a running countdown under the empty state is a panel
      // still half-showing a room that no longer exists.
      setRoom(null);
      setQuestions([]);
      setOverlayState({ visible: false, hideAt: null });
      capture?.("qa_closed_out");
    } catch (error) {
      setProblem(describe(error));
    } finally {
      setBusy(false);
    }
  }, [magicId, confirmingClose, capture]);

  const setOpen = useCallback(
    async (open: boolean) => {
      if (!magicId) return;
      setBusy(true);
      setProblem(null);
      try {
        const next = await setQaOpen(magicId, open);
        setRoom(next);
        capture?.(open ? "qa_reopened" : "qa_closed");
      } catch (error) {
        setProblem(describe(error));
      } finally {
        setBusy(false);
      }
    },
    [magicId, capture],
  );

  // The host's own countdown, computed from the absolute hideAt exactly as the
  // extension computes the overlay's. One source of truth means the card cannot
  // say "12s left" while the projector has already dropped it.
  const secondsLeft =
    overlay.visible && overlay.hideAt
      ? Math.max(0, Math.round((new Date(overlay.hideAt).getTime() - Date.now()) / 1000))
      : null;

  /**
   * The span the bar is drawn against.
   *
   * Deliberately measured from the moment this hideAt arrived, not from the
   * preset the host pressed: after a reload the panel restores mid-countdown
   * and has no idea which preset that was. Measuring what is left when we learn
   * of it makes the bar start full and reach zero on time in both cases —
   * honest about the remaining time, which is the only thing it is claiming.
   */
  const totalSeconds = useMemo(() => {
    if (!overlay.hideAt) return null;
    return Math.max(
      1,
      Math.round((new Date(overlay.hideAt).getTime() - Date.now()) / 1000),
    );
  }, [overlay.hideAt]);

  useEffect(() => {
    if (secondsLeft === null) return undefined;
    if (secondsLeft === 0) {
      // Expired locally. The extension drops it from the same timestamp, so
      // this needs no server round-trip — it is the same arithmetic, not a
      // second opinion.
      setOverlayState({ visible: false, hideAt: null });
      return undefined;
    }
    const timer = window.setTimeout(() => setTick((n) => n + 1), 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);

  const showOverlay = async (seconds: number | null) => {
    if (!magicId) return;
    // Optimistic: the host taps and looks up at the screen, not back at the
    // phone. Waiting on the round-trip to move the card makes the tap feel lost.
    const optimistic: OverlayState = {
      visible: true,
      hideAt: seconds ? new Date(Date.now() + seconds * 1000).toISOString() : null,
    };
    setOverlayState(optimistic);
    setProblem(null);

    try {
      setOverlayState(await setOverlay(magicId, true, seconds));
      capture?.("qa_overlay_shown", { seconds });
    } catch (error) {
      setOverlayState({ visible: false, hideAt: null });
      setProblem(describe(error));
    }
  };

  const hideOverlay = async () => {
    if (!magicId) return;
    setOverlayState({ visible: false, hideAt: null });
    try {
      await setOverlay(magicId, false, null);
      capture?.("qa_overlay_hidden");
    } catch (error) {
      setProblem(describe(error));
    }
  };

  const moderate = async (
    question: HostQuestion,
    patch: { hidden?: boolean; answered?: boolean; pinned?: boolean },
  ) => {
    if (!magicId) return;

    // Optimistic: the host is mid-sentence and the stream's next tick is up to a
    // second and a half away. A button that visibly does nothing for that long
    // gets pressed again.
    setQuestions((previous) =>
      previous.map((q) =>
        q.id === question.id
          ? {
              ...q,
              hidden: patch.hidden ?? q.hidden,
              answered: patch.answered ?? q.answered,
              pinned: patch.pinned ?? q.pinned,
            }
          : q,
      ),
    );

    try {
      await moderateQuestion(magicId, question.id, patch);
    } catch (error) {
      setProblem(describe(error));
      // The stream re-asserts the truth on its next frame, so no manual rollback.
    }
  };

  // --- before there is a room ----------------------------------------------
  //
  // ONE empty state, whatever the plan. There used to be three — sign in,
  // upgrade, turn on — each with its own wording and its own button, which
  // meant the feature introduced itself differently depending on something the
  // reader cannot see. Worse, two of the three sold before they explained.
  //
  // So everybody gets the same pitch and the same button, and the plan decides
  // only what happens AFTER the tap: a host who cannot start Q&A yet gets the
  // dialog that fixes that. The gate stays exactly where it was; it just stops
  // being the first thing the panel says.

  if (!room) {
    // Restoring only ever runs for an entitled host (see the effect), so the
    // others never wait on it.
    //
    // There is no anonymous branch any more: the controller no longer opens
    // without an account, so the honest ask is the purchase.
    const entitled = isPro;

    const start = () => {
      if (!isPro) return onUpgrade("qa_panel");
      void turnOn();
    };

    // Nothing under the button, whatever the plan. Every version of that line
    // was answering a question the reader has not asked yet — the paywall, what the code does — and each one gave the panel a trailing
    // caveat that pulled the eye back off the CTA it had just landed on.
    return (
      <Section>
        <Empty>
          {/* Named here, unlike the running panel below: this is an offer, and
              an offer has to say what it is for. */}
          <EmptyTitle>Audience Q&amp;A</EmptyTitle>

          <EmptyBody>
            <QaIllustration />
            <EmptyText>Let the room ask questions from their phones.</EmptyText>
            {/* Disabled while the restore is in flight, and saying so. A
                button reading "Start Q&A" for the half-second after a reload is
                a button that tells the host their room is gone — and the one
                thing they must not have to wonder about mid-talk is whether the
                code on the wall is still live. */}
            <Action
              type="button"
              onClick={start}
              disabled={busy || restoring || (entitled && !magicId)}
            >
              {busy ? "Starting…" : restoring ? "Checking…" : "Start Q&A"}
            </Action>
          </EmptyBody>
        </Empty>
        {problem && <Problem role="status">{problem}</Problem>}
      </Section>
    );
  }

  return (
    <Section>
      {/* No "Audience Q&A" heading here. The rail's own Q&A tab is showing and
          already names the section; repeating it spends the top of the panel on
          a word the host just clicked. The unentitled states above DO keep it —
          they are an offer, and an offer has to say what it is for. */}
      {room.open ? (
        <SetupBlock>
          <RoomCard>
            <JoinRow>
              <QrTile>
                <QRCodeSVG value={audienceUrlFor(room.code)} size={72} />
              </QrTile>
              <JoinText>
                <Code aria-label={`Room code ${room.code}`}>{room.code}</Code>
              </JoinText>
            </JoinRow>

            <CardRule />

            {/* For the people who are not in the room. The QR is for the ones
                looking at the screen; a link is the only thing that reaches a
                remote audience in a call or a chat thread. */}
            <ShareSection>
              <ShareRow>
                <ShareInput
                  readOnly
                  aria-label="Q&A join link"
                  value={audienceUrlFor(room.code)}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <CopyButton
                  type="button"
                  $done={copied}
                  onClick={() =>
                    canShare
                      ? void shareLink(audienceUrlFor(room.code))
                      : copyShareLink(audienceUrlFor(room.code))
                  }
                  aria-label={
                    copied
                      ? "Copied"
                      : canShare
                        ? "Share Q&A join link"
                        : "Copy Q&A join link"
                  }
                  title={copied ? "Copied" : "Copy link"}
                >
                  {copied ? (
                    <CopiedIcon />
                  ) : canShare ? (
                    <ShareIcon />
                  ) : (
                    <CopyIcon />
                  )}
                  <CopyLabel>
                    {copied ? "Copied" : canShare ? "Share link" : "Copy link"}
                  </CopyLabel>
                </CopyButton>
              </ShareRow>
            </ShareSection>

            <CardRule />

            {/* Show the code ON the presented screen — in the same card as the
                code, because it is an action on it.

                Usable before the extension ships that overlay: the card is right
                here, so a host can hold the phone up or read the six characters
                out. The presets only reach the projector once the extension
                build lands, and nothing here depends on it. */}
            <DeckSection>
              <CardLabel>Show QR code on deck</CardLabel>
              {overlay.visible ? (
                <LiveTile>
                  <LiveRow>
                    <LiveDot aria-hidden="true" />
                    {/* One live region for the whole state, so a screen reader
                        is told "on your deck, hiding in 18s" rather than being
                        read a bare number every second. */}
                    <LiveText role="status">
                      <LiveLabel>On your deck</LiveLabel>
                      <VisuallyHidden>
                        {secondsLeft === null
                          ? " — until you hide it"
                          : ` — hiding in ${secondsLeft} seconds`}
                      </VisuallyHidden>
                      <Countdown aria-hidden="true">
                        {secondsLeft === null ? "No timer" : `${secondsLeft}s`}
                      </Countdown>
                    </LiveText>
                    <HideNow type="button" onClick={() => void hideOverlay()}>
                      <EyeOffIcon />
                      Hide
                    </HideNow>
                  </LiveRow>
                  {/* Only where there is something to run down. An open-ended
                      overlay has no proportion to draw, and a bar sitting full
                      forever would read as a stuck timer. */}
                  {totalSeconds !== null && secondsLeft !== null && (
                    <LiveBar
                      $pct={(secondsLeft / totalSeconds) * 100}
                      aria-hidden="true"
                    />
                  )}
                </LiveTile>
              ) : (
                <Presets>
                  {PRESETS.map((preset) => (
                    <Preset
                      key={preset.label}
                      type="button"
                      $wide={preset.seconds === null}
                      onClick={() => void showOverlay(preset.seconds)}
                      disabled={!magicId}
                    >
                      {preset.label}
                    </Preset>
                  ))}
                </Presets>
              )}
            </DeckSection>
          </RoomCard>
        </SetupBlock>
      ) : (
        <ClosedNote>
          Questions are paused. The room keeps the code{" "}
          <strong>{room.code}</strong> — resuming does not change it.
        </ClosedNote>
      )}

      {problem && <Problem role="status">{problem}</Problem>}

      {/* The queue. Its own group with a count in the heading, because once
          questions start arriving this is the part of the panel the host is
          actually working — everything above is setup they did once. */}
      {questions.length > 0 && (
        <Group $stageFirst>
          <GroupHead>
            <Label>Questions</Label>
            <Waiting>{pending} waiting</Waiting>
          </GroupHead>
          <Questions>
          {questions.map((question) => (
            <Item key={question.id} $dim={question.hidden || question.answered}>
              <ItemHead>
                <Votes>{question.votes}</Votes>
                <span>{question.authorName ?? "Anonymous"}</span>
                {/* The differentiator, and the reason to read this panel at all:
                    four questions on slide 12 means slide 12 is confusing.

                    Slides are 1-based here, so a 0 is not slide zero — it is a
                    deck that had not started when the question arrived, and
                    printing "Slide 0" invites a hunt for a slide that does not
                    exist. */}
                {question.slideIndex != null && question.slideIndex > 0 && (
                  <span>· Slide {question.slideIndex}</span>
                )}
              </ItemHead>
              <QuestionText>{question.text}</QuestionText>
              <Actions>
                {/* "Answer", not "Mark answered": two words wrapped onto two
                    lines in the rail and made this row twice as tall as the
                    other two. It matches its siblings' shape as well — verb
                    when it is an action, adjective once it has happened, the
                    same as Pin/Pinned — and the title says the rest. */}
                <MiniButton
                  type="button"
                  $active={question.answered}
                  onClick={() => void moderate(question, { answered: !question.answered })}
                  title={
                    question.answered
                      ? "Answered — click to undo"
                      : "Mark this answered"
                  }
                >
                  {question.answered ? "Answered" : "Answer"}
                </MiniButton>
                <MiniButton
                  type="button"
                  $active={question.pinned}
                  onClick={() => void moderate(question, { pinned: !question.pinned })}
                >
                  {question.pinned ? "Pinned" : "Pin"}
                </MiniButton>
                <MiniButton
                  type="button"
                  onClick={() => void moderate(question, { hidden: !question.hidden })}
                  title={
                    question.hidden
                      ? "Show this to the room again"
                      : "Hide this from the room"
                  }
                >
                  {question.hidden ? "Unhide" : "Hide"}
                </MiniButton>
              </Actions>
              </Item>
            ))}
          </Questions>
        </Group>
      )}

      {/* Two controls, and only two. Pausing is the reversible one and reads as
          ordinary; closing is the one that destroys the room, so it is outlined
          in red and asks first. Shown in both states — a paused room still has
          to be closable, and the resume is here rather than as a separate
          headline button so there is one place to look either way. */}
      <Foot>
        <QuietAction
          type="button"
          onClick={() => {
            setConfirmingClose(false);
            void setOpen(!room.open);
          }}
          disabled={busy}
          title={
            room.open
              ? "Stop taking new questions. The code keeps working."
              : "Start taking questions again on the same code"
          }
        >
          {room.open ? "Pause questions" : "Resume questions"}
        </QuietAction>
        <QuietAction
          type="button"
          $danger
          $armed={confirmingClose}
          onClick={() => void endQa()}
          disabled={busy}
          title={
            confirmingClose
              ? "This deletes every question and stops the code working"
              : "Close Q&A — the code stops working and every question is deleted"
          }
        >
          {confirmingClose ? "Delete everything?" : "Close Q&A"}
        </QuietAction>
      </Foot>
    </Section>
  );
};
